import * as React from "react";
import { LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui";
import { HoloBorder, SocoAiLogo } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { DesignPackageListParams, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { useDesignPackages } from "../hooks/useDesignPackages";
import { PackageCard } from "./PackageCard";

export const INSPIRATION_PARAMS: DesignPackageListParams = {
  scope: "global",
  status: "published",
  perPage: 6,
  sort: "publishedAt",
  order: "desc",
};

export const GLOBALS_STALE_TIME = 5 * 60_000;
export const INSPIRATION_HIDDEN_KEY = "promptStudio.inspirationHidden";

export function useInspirationPackages(enabled = true) {
  return useDesignPackages(INSPIRATION_PARAMS, { enabled, staleTime: GLOBALS_STALE_TIME });
}

export interface InspirationStripProps {
  expanded?: boolean;
  onPreview(pkg: DesignPackageSummary): void;
  onViewAll(): void;
  onHide?(): void;
}

const ViewAllCard: React.FC<{ expanded?: boolean; onClick(): void }> = ({ expanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "shrink-0 snap-start rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-400 flex flex-col items-center justify-center gap-2 text-sm font-semibold text-slate-500",
      expanded ? "w-[160px]" : "w-[120px]"
    )}
  >
    <LayoutGrid className="size-5" aria-hidden="true" />
    View all
  </button>
);

/** The page renders the loading skeleton; the strip itself stays out of the way until it has globals. */
export const InspirationStrip: React.FC<InspirationStripProps> = ({ expanded, onPreview, onViewAll, onHide }) => {
  const { data, isLoading, isError } = useInspirationPackages();
  const packages = data?.content ?? [];

  if (isLoading || isError || packages.length === 0) return null;

  const tileWidth = expanded ? "w-[240px]" : "w-[200px]";

  return (
    <HoloBorder radius="2xl" active={false}>
      <div className="p-5 flex flex-col gap-4" data-testid="inspiration-strip">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <SocoAiLogo variant="mono" height={14} />
              <span className="text-lg font-bold text-slate-800">Need some inspiration?</span>
            </div>
            <span className="text-sm text-slate-500">
              Fresh designs handpicked by the SocialCoach team. Add one to your designs, make it yours, and publish it
              to your players.
            </span>
          </div>
          {!expanded && onHide && (
            <Button variant="link" size="sm" onClick={onHide}>
              Hide
            </Button>
          )}
        </div>
        <div className="flex gap-3 overflow-x-auto snap-x pb-1">
          {packages.map(pkg => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              variant="global"
              showGlobalBadge={false}
              onClick={() => onPreview(pkg)}
              className={cn("shrink-0 snap-start", tileWidth)}
            />
          ))}
          <ViewAllCard expanded={expanded} onClick={onViewAll} />
        </div>
      </div>
    </HoloBorder>
  );
};
